// @ts-check
/**
 * Module core/highlight
 *
 * Performs syntax highlighting to all pre and code elements.
 */
import css from "../styles/highlight.css.js";
import { insertStyle } from "./insert-style.js";
import { msgIdGenerator } from "./utils.js";
import { workerPromise } from "./worker.js";
export const name = "core/highlight";

const nextMsgId = msgIdGenerator("highlight");

/**
 * @param {DOMTokenList} classList
 */
function getLanguageHint(classList) {
  return Array.from(classList)
    .filter(item => item !== "highlight" && item !== "nolinks")
    .map(item => item.toLowerCase());
}

/**
 * @param {HTMLElement} elem
 */
async function highlightElement(elem) {
  const htmlElem = elem;
  htmlElem.setAttribute("aria-busy", "true");
  const languages = getLanguageHint(htmlElem.classList);
  let response;
  try {
    response = await sendHighlightRequest(htmlElem.textContent, languages);
  } catch (err) {
    console.error(err);
    return;
  }
  const { language, value } = response;
  switch (htmlElem.localName) {
    case "pre":
      htmlElem.classList.remove(language);
      htmlElem.innerHTML = `<code class="hljs${
        language ? ` ${language}` : ""
      }">${value}</code>`;
      if (!htmlElem.classList.length) htmlElem.removeAttribute("class");
      break;
    case "code":
      htmlElem.innerHTML = value;
      htmlElem.classList.add("hljs");
      if (language) htmlElem.classList.add(language);
      break;
  }
  htmlElem.setAttribute("aria-busy", "false");
}

/**
 * @param {string} code
 * @param {string[]} languages
 */
async function sendHighlightRequest(code, languages) {
  const msg = {
    action: "highlight",
    code,
    id: nextMsgId(),
    languages,
  };
  const worker = await workerPromise;
  worker.postMessage(msg);
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error("Timed out waiting for highlight."));
    }, 4000);
    worker.addEventListener("message", function listener(ev) {
      const {
        data: { id, language, value },
      } = ev;
      if (id !== msg.id) return; // not for us!
      worker.removeEventListener("message", listener);
      clearTimeout(timeoutId);
      resolve({ language, value });
    });
  });
}

/**
 * @param {Conf} conf
 */
export async function run(conf) {
  // Nothing to highlight
  if (conf.noHighlightCSS) return;
  const highlightables = [
    .../** @type {NodeListOf<HTMLElement>} */ (
      document.querySelectorAll(`
    pre:not(.idl):not(.cddl):not(.nohighlight) > code:not(.nohighlight),
    pre:not(.idl):not(.cddl):not(.nohighlight),
    code.highlight
  `)
    ),
  ].filter(
    // Filter pre's that contain code
    elem => elem.localName !== "pre" || !elem.querySelector("code")
  );
  // Nothing to highlight
  if (!highlightables.length) {
    return;
  }
  const promisesToHighlight = highlightables
    .filter(elem => elem.textContent.trim())
    .map(highlightElement);
  insertStyle(css);
  await Promise.all(promisesToHighlight);
}
